import httpClient from '@utils/httpClient';
import { Program, ChecklistCategory, ChecklistItem, User, ProgramStats, ProgramProgressItem } from '@modules/programManager/types';

class ApiService {
    constructor() {
    }

    // Program APIs
    async getPrograms(): Promise<Program[]> {
        const response = await httpClient.get('/programs');
        return response.programs;
    }

    // Get only sessions (program_type = 1)
    async getSessionPrograms(): Promise<Program[]> {
        const response = await httpClient.get('/programs/sessions');
        return response.programs;
    }

    async getProgramById(id: string): Promise<Program> {
        const response = await httpClient.get(`/programs/${id}`);
        return response.program;
    }

    async createProgram(program: Omit<Program, 'id' | 'createdAt' | 'updatedAt'>): Promise<Program> {
        console.log('🚀 Frontend createProgram API called with:', program);
        const response = await httpClient.post('/programs', program);
        console.log('✅ Frontend createProgram API response:', response);
        return response.program;
    }

    async updateProgram(id: string, program: Partial<Program>): Promise<Program> {
        const response = await httpClient.put(`/programs/${id}`, program);
        return response.program;
    }

    async deleteProgram(id: string): Promise<void> {
        await httpClient.delete(`/programs/${id}`);
    }

    // Checklist Category APIs
    async getChecklistCategories(): Promise<ChecklistCategory[]> {
        const response = await httpClient.get('/categories');
        return response.categories || [];
    }

    // Get categories (alias for getChecklistCategories)
    async getCategories(): Promise<ChecklistCategory[]> {
        return this.getChecklistCategories();
    }

    // Get program checklist items
    async getProgramChecklist(programId: string | number): Promise<any[]> {
        const response = await httpClient.get(`/programs/${programId}/progress`);
        return response.data || [];
    }

    // Get sessions stats
    async getSessionsStats(): Promise<any> {
        const response = await httpClient.get('/programs/sessions/stats');
        return response.stats || {};
    }

    // Get all tasks for task management
    async getAllTasks(): Promise<ChecklistItem[]> {
        const response = await httpClient.get('/tasks');
        return response.items || [];
    }

    async createChecklistCategory(category: { name: string; description?: string; order: number }): Promise<ChecklistCategory> {
        const response = await httpClient.post('/checklist/categories', category);
        return response.category;
    }

    async updateChecklistCategory(id: string, category: { name: string; description?: string; order: number }): Promise<ChecklistCategory> {
        const response = await httpClient.put(`/checklist/categories/${id}`, category);
        return response.category;
    }

    async deleteChecklistCategory(id: string): Promise<void> {
        await httpClient.delete(`/checklist/categories/${id}`);
    }

    // Checklist Item APIs
    async getChecklistItems(programId: string): Promise<ChecklistItem[]> {
        const response = await httpClient.get(`/programs/${programId}/checklist`);
        return response.checklist;
    }

    async createChecklistItem(programId: string, item: Omit<ChecklistItem, 'id' | 'programId' | 'createdAt' | 'updatedAt'>): Promise<ChecklistItem> {
        const response = await httpClient.post(`/programs/${programId}/checklist`, item);
        return response.item;
    }

    async updateChecklistItem(programId: string, itemId: string, item: Partial<ChecklistItem>): Promise<ChecklistItem> {
        const response = await httpClient.put(`/programs/${programId}/checklist/${itemId}`, item);
        return response.item;
    }

    async deleteChecklistItem(programId: string, itemId: string): Promise<void> {
        await httpClient.delete(`/programs/${programId}/checklist/${itemId}`);
    }


    // Auth APIs
    async login(email: string, password: string): Promise<{ token: string; user: User }> {
        const response = await httpClient.post('/login', { email, password });

        // Store token in localStorage
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        localStorage.setItem('isAuthenticated', 'true');

        return { token: response.token, user: response.user };
    }

    async logout(): Promise<void> {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
    }

    isAuthenticated(): boolean {
        return localStorage.getItem('authToken') !== null;
    }

    // User APIs
    async getUsers(): Promise<User[]> {
        const response = await httpClient.get('/users');
        return response.users;
    }

    // Stats APIs
    async getProgramStats(): Promise<ProgramStats> {
        const response = await httpClient.get('/programs/stats');
        return response.stats;
    }

    // Bulk Operations
    async bulkUpdateChecklistItems(programId: string, itemIds: string[], updateData: Partial<ChecklistItem>): Promise<void> {
        await httpClient.put(`/programs/${programId}/checklist/bulk`, {
            itemIds,
            updateData
        });
    }

    async bulkDeleteChecklistItems(programId: string, itemIds: string[]): Promise<void> {
        await httpClient.delete(`/programs/${programId}/checklist/bulk`, {
            data: { itemIds }
        });
    }

    // Export/Import
    async exportChecklist(programId: string, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
        const response = await httpClient.get(`/programs/${programId}/checklist/export?format=${format}`, {
            responseType: 'blob'
        });
        return response;
    }

    async importChecklist(programId: string, file: File): Promise<{ success: boolean; message: string; imported: number }> {
        const formData = new FormData();
        formData.append('file', file);
        const response = await httpClient.post(`/programs/${programId}/checklist/import`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response;
    }

    // Program Progress APIs
    async getProgramProgress(programId: string): Promise<ProgramProgressItem[]> {
        const response = await httpClient.get(`/programs/${programId}/progress`);
        return response.data;
    }

    // Get all program progress tasks (all 36 tasks)
    async getAllProgramProgress(): Promise<ProgramProgressItem[]> {
        const response = await httpClient.get('/programs/progress/all');
        return response.data;
    }

    async updateProgramStatus(programId: string, status: string): Promise<Program> {
        const response = await httpClient.put(`/programs/${programId}/status`, { status });
        return response.program;
    }

    async createProgramProgress(programId: string, progressData: Partial<ProgramProgressItem>): Promise<ProgramProgressItem> {
        const response = await httpClient.post(`/programs/${programId}/progress`, progressData);
        return response.data;
    }

    async updateProgramProgress(programId: string, itemId: string, progressData: Partial<ProgramProgressItem>): Promise<ProgramProgressItem> {
        const response = await httpClient.put(`/programs/${programId}/progress/${itemId}`, progressData);
        return response.data;
    }

    async deleteProgramProgress(programId: string, itemId: string): Promise<void> {
        await httpClient.delete(`/programs/${programId}/progress/${itemId}`);
    }

    // Mark all tasks complete for a program
    async markAllTasksComplete(programId: string): Promise<{
        success: boolean;
        message: string;
        updatedCount: number;
        taskSummary: {
            pending: number;
            inProgress: number;
            completed: number;
            total: number;
        };
        programStatus: string;
    }> {
        const response = await httpClient.put(`/programs/${programId}/complete-all`);
        return response;
    }

    // Get task summary for a program (for mark all complete dialog)
    async getTaskSummary(programId: string): Promise<{
        pending: number;
        inProgress: number;
        completed: number;
        cancelled: number;
        total: number;
        toBeUpdated: number;
    }> {
        const response = await httpClient.get(`/programs/${programId}/task-summary`);
        return response.summary;
    }

}

export const apiService = new ApiService();
